/**
 * redirectViewportView — project a `ViewportEntity` onto the `ViewportLike`
 * shape the MSPACE_VIA_VP redirect context hands to the pointer / wheel / pan
 * paths (`activeInputSpace`, `stageInteractions`, `draftPreviewMapping`).
 *
 * Extracted from `Scene3DStage`'s `viewportRedirectRef.getActiveViewport`
 * closure so the projection is unit-testable on its own. It is a SYNTHETIC
 * literal, not a spread — that is deliberate (the redirect contract is a
 * minimal structural view, not the whole entity), and it is exactly why it is
 * a place a newly-required field gets silently dropped. `viewTarget` is the
 * field that bit: `paperToModel` anchors the VP window on `viewTarget +
 * R(viewTwistAngle)·viewCenter` (groups 12/22 are DCS, 17/27 is the WCS point
 * the DCS origin sits at, and the twist turns the DCS axes —
 * `drawing-model`'s `viewportWcsViewCenter`), which is the SAME
 * anchor the per-VP camera, the cull rect, the VPCLIP stencil and the
 * membership index use. Dropping it here puts every in-VP pick / OSNAP /
 * window-select / draft point a whole target vector from what is painted.
 *
 * THREE-free: `readViewportOverrides` comes from `drawing-model`, which has no
 * `three` dependency.
 */

import {
  readViewportOverrides,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";

import type { ViewportLike } from "./viewportTransform2d";

/** The redirect context's view of the active viewport. */
export type RedirectViewportView = ViewportLike & {
  id: string;
  effectiveLocked: boolean;
  frozenLayers?: readonly string[];
};

/**
 * Resolve `activeViewportId` against the live layout viewport list and project
 * the match. Returns null when the id is absent or no longer resolves (e.g.
 * the VP was deleted mid-gesture).
 *
 * `effectiveLocked` is the AutoCAD effective lock: the entity's own
 * `displayLocked` OR the MC5 XDATA override (`EDIT_DWG_VP_OVERRIDES`).
 */
export function resolveRedirectViewportView(
  viewports: readonly ViewportEntity[],
  activeViewportId: string | null,
): RedirectViewportView | null {
  if (activeViewportId == null) return null;
  const vp = viewports.find((v) => v.id === activeViewportId);
  if (!vp) return null;
  const effectiveLocked =
    vp.displayLocked === true || readViewportOverrides(vp).locked;
  return {
    id: vp.id,
    center: vp.center,
    width: vp.width,
    height: vp.height,
    viewCenter: vp.viewCenter,
    // DXF 17/27 — half the window anchor; see the module doc for why it must
    // be carried through this literal.
    viewTarget: vp.viewTarget,
    viewHeight: vp.viewHeight,
    viewTwistAngle: vp.viewTwistAngle,
    effectiveLocked,
    // MC4 S2 P2 (#D) — per-VP frozen layers (same source the cull path reads,
    // `viewportCullingAdapter` keys on `vp.frozenLayers`). Lets the in-VP
    // pick/drag paths exclude candidates frozen in this VP.
    frozenLayers: vp.frozenLayers,
  };
}
